// src/components/community/CommentItem.js
import React, { useRef, useEffect } from 'react';
import CommentForm from './CommentForm';
import "./Comments.css";

const CommentItem = ({ 
  comment, 
  currentUser, 
  onReply, 
  isReplying, 
  onCancelReply,
  onSubmitReply,
  onEdit,
  isEditing,
  onCancelEdit,
  onSubmitEdit,
  onDelete
}) => {
  const isMounted = useRef(true);
  
  // Set up mounted ref
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Format the date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate time difference for "edited X ago"
  const getEditTimeDisplay = (created, updated) => {
    const createdDate = new Date(created);
    const updatedDate = new Date(updated);
    
    if (updatedDate - createdDate < 1000) return '';
    
    const diffInSeconds = Math.floor((updatedDate - createdDate) / 1000);
    
    if (diffInSeconds < 60) {
      return 'just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} after posting`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} ${hours === 1 ? 'hour' : 'hours'} after posting`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} ${days === 1 ? 'day' : 'days'} after posting`;
    }
  };

  // Safe handlers
  const handleReply = () => {
    if (isMounted.current && onReply) {
      onReply();
    }
  };
  
  const handleEdit = () => {
    if (isMounted.current && onEdit) {
      onEdit();
    }
  };
  
  const handleDelete = () => {
    if (isMounted.current && onDelete) {
      onDelete();
    }
  };
  
  const handleSubmitReply = (content) => {
    if (isMounted.current && onSubmitReply) {
      onSubmitReply(content);
    }
  };
  
  const handleSubmitEdit = (content) => {
    if (isMounted.current && onSubmitEdit) {
      onSubmitEdit(content);
    }
  };

  // Check if comment is deleted
  const isDeleted = comment.content === '[Comment deleted]' || !comment.userId;

  return (
    <div className="comment-item">
      <div className="comment-main">
        <div className="comment-header">
          {!isDeleted ? (
            <div className="comment-user">
              <img 
                src={comment.userId.profilePicture || "/default-avatar.png"} 
                alt={comment.userId.username} 
                className="comment-user-avatar"
              />
              <span className="comment-username">{comment.userId.username}</span>
              <span className="comment-date">{formatDate(comment.createdAt)}</span>
              {comment.isEdited && (
                <span className="comment-edited" title={getEditTimeDisplay(comment.createdAt, comment.updatedAt)}>
                  (edited)
                </span>
              )}
            </div>
          ) : (
            <div className="comment-deleted-user">
              <span>Deleted User</span>
              <span className="comment-date">{formatDate(comment.createdAt)}</span>
            </div>
          )}
        </div>
        
        {isEditing ? (
          <CommentForm 
            initialValue={comment.content}
            onSubmit={handleSubmitEdit}
            onCancel={onCancelEdit}
            submitButtonText="Update"
            placeholder="Edit your comment..."
          />
        ) : (
          <div className="comment-content">
            {comment.content}
          </div>
        )}
        
        {!isEditing && !isDeleted && currentUser && (
          <div className="comment-actions">
            <button 
              className="reply-button"
              onClick={handleReply}
            >
              Reply
            </button>
            
            {currentUser.id === comment.userId.id && (
              <>
                <button 
                  className="edit-button"
                  onClick={handleEdit}
                >
                  Edit
                </button>
                <button 
                  className="delete-button"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </>
            )}
          </div>
        )}
        
        {isReplying && (
          <div className="reply-form">
            <CommentForm 
              onSubmit={handleSubmitReply}
              onCancel={onCancelReply}
              placeholder={`Reply to ${comment.userId.username}...`}
            />
          </div>
        )}
      </div>
      
      {comment.replies && comment.replies.length > 0 && (
        <div className="comment-replies">
          {comment.replies.map(reply => (
            <CommentItem 
              key={reply.id}
              comment={reply}
              currentUser={currentUser}
              onReply={() => onReply(reply.id)}
              isReplying={isReplying === reply.id}
              onCancelReply={onCancelReply}
              onSubmitReply={(content) => onSubmitReply(content, reply.id)}
              onEdit={() => onEdit(reply.id)}
              isEditing={isEditing === reply.id}
              onCancelEdit={onCancelEdit}
              onSubmitEdit={(content) => onSubmitEdit(reply.id, content)}
              onDelete={() => onDelete(reply.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentItem;